import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormInput,
  FormRoot,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  BellRingIcon,
  ClockIcon,
  GaugeIcon,
  ServerIcon,
  SettingsIcon,
} from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import {
  usePackageNotificationConfigQuery,
  usePackageNotificationOpsStatusQuery,
  useUpdatePackageNotificationConfig,
  useWhatsAppWorkerStatusQuery,
} from "../../api/queries";
import { PackageNotificationsOpsPanel } from "../../ui/PackageNotificationsOpsPanel";
import { WorkerStatusBanner } from "../../ui/WorkerStatusBanner";
import { WhatsAppWorkerConnectedPanel } from "../../ui/WhatsAppWorkerConnectedPanel";
import { WhatsAppWorkerQrPanel } from "../../ui/WhatsAppWorkerQrPanel";

const SettingsFormSchema = z.object({
  thresholdSessions: z.coerce.number().int().min(1),
  thresholdHours: z.coerce.number().min(0.25),
  daysBeforeExpiry: z.coerce.number().int().min(1),
  dedupeDays: z.coerce.number().int().min(1),
  sendWindowStart: z.string(),
  sendWindowEnd: z.string(),
  maxAutoPerDay: z.coerce.number().int().min(1),
  warmupMaxPerDay: z.coerce.number().int().min(1),
  warmupUntil: z.string().nullable().optional(),
  workerBaseUrl: z.string().nullable().optional(),
  workerApiKey: z.string().nullable().optional(),
  enabled: z.boolean().optional(),
  autoEnabled: z.boolean().optional(),
});

type SettingsFormValues = z.infer<typeof SettingsFormSchema>;

export function PackageNotificationsSettingsPage() {
  const nav = useNavigate();
  const configQuery = usePackageNotificationConfigQuery();
  const opsStatusQuery = usePackageNotificationOpsStatusQuery();
  const updateConfig = useUpdatePackageNotificationConfig();
  const workerQuery = useWhatsAppWorkerStatusQuery({ refetchInterval: 3_000 });

  const methods = RHF.useForm<SettingsFormValues>({
    // @ts-expect-error - zodResolver type mismatch with z.coerce
    resolver: zodResolver(SettingsFormSchema),
    defaultValues: {
      enabled: false,
      autoEnabled: false,
    },
  });

  useEffect(() => {
    if (!configQuery.data) return;
    methods.reset({
      thresholdSessions: configQuery.data.thresholdSessions,
      thresholdHours: configQuery.data.thresholdHours,
      daysBeforeExpiry: configQuery.data.daysBeforeExpiry,
      dedupeDays: configQuery.data.dedupeDays,
      sendWindowStart: configQuery.data.sendWindowStart,
      sendWindowEnd: configQuery.data.sendWindowEnd,
      maxAutoPerDay: configQuery.data.maxAutoPerDay,
      warmupMaxPerDay: configQuery.data.warmupMaxPerDay,
      warmupUntil: configQuery.data.warmupUntil ?? "",
      workerBaseUrl: configQuery.data.workerBaseUrl ?? "",
      workerApiKey: "",
      enabled: configQuery.data.enabled,
      autoEnabled: configQuery.data.autoEnabled,
    });
  }, [configQuery.data, methods]);

  const onSubmit = async (values: SettingsFormValues) => {
    try {
      await updateConfig.mutateAsync({
        ...values,
        warmupUntil: values.warmupUntil?.trim() ? values.warmupUntil.trim() : null,
        workerBaseUrl: values.workerBaseUrl?.trim() || null,
        workerApiKey:
          values.workerApiKey?.trim() === "" ||
          values.workerApiKey === "••••••••"
            ? undefined
            : values.workerApiKey ?? null,
      });
      toast.success("ບັນທຶກການຕັ້ງຄ່າສຳເລັດ");
    } catch {
      toast.error("ບັນທຶກການຕັ້ງຄ່າບໍ່ສຳເລັດ");
    }
  };

  const showQrPanel =
    workerQuery.data?.needsQr === true ||
    workerQuery.data?.status === "needs_qr" ||
    workerQuery.data?.status === "initializing";

  const isWorkerConnected =
    workerQuery.data?.connected === true ||
    workerQuery.data?.status === "connected";

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            {
              label: "ແຈ້ງເຕືອນແພັກ",
              to: "/app/package-notifications",
            },
            { label: "ຕັ້ງຄ່າ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mx-auto w-full max-w-6xl space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
                  <SettingsIcon className="size-6 shrink-0" />
                  ຕັ້ງຄ່າແຈ້ງເຕືອນແພັກ WhatsApp
                </h2>
                <WorkerStatusBanner
                  health={workerQuery.data}
                  embeddedOnSettingsPage
                />
              </div>
              <p className="mt-1 text-muted-foreground">
                ກຳນົດເງື່ອນໄຂແພັກ, ເຊື່ອມ Worker, ຊ່ວງເວລາສົ່ງ ແລະ
                ຈຳນວນຂໍ້ຄວາມອັດຕະໂນມັດ
              </p>
            </div>
            <Button
              variant="outline"
              className="shrink-0"
              onClick={() => nav({ to: "/app/package-notifications" })}
            >
              <ArrowLeftIcon className="mr-1 size-4" />
              ກັບຄືນ
            </Button>
          </div>

          {showQrPanel && !isWorkerConnected ? <WhatsAppWorkerQrPanel /> : null}
          {isWorkerConnected ? <WhatsAppWorkerConnectedPanel /> : null}

          <PackageNotificationsOpsPanel
            data={opsStatusQuery.data}
            isLoading={opsStatusQuery.isLoading}
            isFetching={opsStatusQuery.isFetching}
            onRefresh={() => {
              void opsStatusQuery.refetch();
            }}
          />

          <QueryState
            result={configQuery}
            title="ກຳລັງໂຫຼດການຕັ້ງຄ່າ"
            description="ກຳລັງດຶງຂໍ້ມູນຈາກເຊີບເວີ"
          >
            <FormRoot
              methods={methods}
              onSubmit={onSubmit}
              className="grid gap-6 lg:grid-cols-2"
            >
              <Card className="gap-0 h-full">
              <CardHeader className="space-y-1 pb-3">
                <div className="flex items-center gap-2">
                  <BellRingIcon className="size-5 text-primary" />
                  <CardTitle>ເປີດໃຊ້ງານ</CardTitle>
                </div>
                <CardDescription>
                  ເປີດ/ປິດການແຈ້ງເຕືອນແພັກເກດຜ່ານ WhatsApp
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <FormCheckboxCard
                  name="enabled"
                  label="ເປີດໃຊ້ງານລະບົບແຈ້ງເຕືອນ"
                  description="ຖ້າປິດ — ຈະບໍ່ enqueue ຫຼືສົ່ງແຈ້ງເຕືອນ (ບໍ່ກະທົບ wa.me manual)"
                />
                <FormCheckboxCard
                  name="autoEnabled"
                  label="ສົ່ງອັດຕະໂນມັດ (Scheduler)"
                  description="cron ສົ່ງ 09:30 ທຸກວັນ (Asia/Vientiane) ຕາມຊ່ວງເວລາ ແລະຈຳນວນຈຳກັດ"
                />
              </CardContent>
            </Card>

            <Card className="gap-0 h-full">
              <CardHeader className="space-y-1 pb-3">
                <div className="flex items-center gap-2">
                  <ServerIcon className="size-5 text-primary" />
                  <CardTitle>WhatsApp Worker</CardTitle>
                </div>
                <CardDescription>
                  URL ແລະລະຫັດ API ຂອງ service ສົ່ງຂໍ້ຄວາມ (Fanglao_WhatsApp_Worker)
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 pt-0">
                <div className="space-y-1">
                  <FormInput
                    name="workerBaseUrl"
                    label="Worker URL"
                    placeholder="http://127.0.0.1:3100"
                  />
                  <p className="text-muted-foreground text-xs">
                    ຕ້ອງເຂົ້າເຖິງໄດ້ຈາກ Admin server (staging/production)
                  </p>
                </div>
                <div className="space-y-1">
                  <FormInput
                    name="workerApiKey"
                    label="Worker API Key"
                    type="password"
                    placeholder="ປ່ອນລະຫັດໃໝ່ (ວ່າງ = ບໍ່ປ່ຽນ)"
                  />
                  <p className="text-muted-foreground text-xs">
                    ຕັ້ງ WA_API_KEY ໃນ Worker ໃຫ້ກົງກັນ
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="gap-0 lg:col-span-2">
              <CardHeader className="space-y-1 pb-3">
                <div className="flex items-center gap-2">
                  <GaugeIcon className="size-5 text-primary" />
                  <CardTitle>ເງື່ອນໄຂແພັກ</CardTitle>
                </div>
                <CardDescription>
                  ນັກຮຽນທີ່ເຂົ້າເງື່ອນໄຂຈະປາກົດໃນລາຍການ eligible
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 pt-0 sm:grid-cols-2 xl:grid-cols-4">
                <div className="space-y-1">
                  <FormInput
                    name="thresholdSessions"
                    label="ແພັກຄລາສ — ເຫຼືອຄັ້ງ (ຕ່ຳກວ່າ)"
                    type="number"
                  />
                  <p className="text-muted-foreground text-xs">ຄ່າເລີ່ມຕົ້ນ: 3 ຄັ້ງ</p>
                </div>
                <div className="space-y-1">
                  <FormInput
                    name="thresholdHours"
                    label="ແພັກສ່ວນຕົວ — ເຫຼືອຊົ່ວໂມງ (ຕ່ຳກວ່າ)"
                    type="number"
                    step="0.25"
                  />
                  <p className="text-muted-foreground text-xs">ຄ່າເລີ່ມຕົ້ນ: 2 ຊົ່ວໂມງ</p>
                </div>
                <div className="space-y-1">
                  <FormInput
                    name="daysBeforeExpiry"
                    label="ໃກ້ໝົດອາຍຸ (ພາຍໃນ N ວັນ)"
                    type="number"
                  />
                  <p className="text-muted-foreground text-xs">ຄ່າເລີ່ມຕົ້ນ: 7 ວັນ</p>
                </div>
                <div className="space-y-1">
                  <FormInput
                    name="dedupeDays"
                    label="ບໍ່ສົ່ງຊ້ຳ (dedupe ວັນ)"
                    type="number"
                  />
                  <p className="text-muted-foreground text-xs">
                    ຂໍ້ຄວາມເດີມບໍ່ສົ່ງຊ້ຳໃນໄລຍະນີ້
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="gap-0 h-full">
              <CardHeader className="space-y-1 pb-3">
                <div className="flex items-center gap-2">
                  <ClockIcon className="size-5 text-primary" />
                  <CardTitle>ຊ່ວງເວລາສົ່ງ</CardTitle>
                </div>
                <CardDescription>
                  ໃຊ້ກັບການສົ່ງອັດຕະໂນມັດ — timezone Asia/Vientiane
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 pt-0 md:grid-cols-2">
                <FormInput
                  name="sendWindowStart"
                  label="ເລີ່ມສົ່ງ (HH:mm)"
                  placeholder="09:00"
                />
                <FormInput
                  name="sendWindowEnd"
                  label="ສິ້ນສຸດສົ່ງ (HH:mm)"
                  placeholder="18:00"
                />
              </CardContent>
            </Card>

            <Card className="gap-0 h-full">
              <CardHeader className="space-y-1 pb-3">
                <div className="flex items-center gap-2">
                  <GaugeIcon className="size-5 text-primary" />
                  <CardTitle>ຈຳນວນສົ່ງອັດຕະໂນມັດ</CardTitle>
                </div>
                <CardDescription>
                  ຈຳกัดຂໍ້ຄວາມຕໍ່ວັນເພື່ອຫຼຸດຄວາມສ່ຽງ WhatsApp ban
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 pt-0 md:grid-cols-2">
                <div className="space-y-1">
                  <FormInput
                    name="maxAutoPerDay"
                    label="ສູງສຸດຕໍ່ວັນ (ຫຼັງ warm-up)"
                    type="number"
                  />
                  <p className="text-muted-foreground text-xs">ຄ່າເລີ່ມຕົ້ນ: 50 ຂໍ້ຄວາມ/ວັນ</p>
                </div>
                <div className="space-y-1">
                  <FormInput
                    name="warmupMaxPerDay"
                    label="ສູງສຸດຕໍ່ວັນ (warm-up)"
                    type="number"
                  />
                  <p className="text-muted-foreground text-xs">
                    ຄ່າເລີ່ມຕົ້ນ: 10 ຂໍ້ຄວາມ/ວັນ ສັບດາຫ໌ທຳອິດ
                  </p>
                </div>
                <div className="space-y-1 md:col-span-2">
                  <FormInput
                    name="warmupUntil"
                    label="Warm-up ຈົນວັນທີ (yyyy-MM-dd)"
                    placeholder="2026-09-30"
                  />
                  <p className="text-muted-foreground text-xs">
                    ກ່ອນວັນນີ້ໃຊ້ warmup max — ຫຼັງຈາກນັ້ນໃຊ້ max ປົກກະຕິ
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-wrap justify-end gap-2 border-t pt-4 lg:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => methods.reset()}
                disabled={updateConfig.isPending}
              >
                ລ້າງ
              </Button>
              <Button type="submit" disabled={updateConfig.isPending}>
                {updateConfig.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກການຕັ້ງຄ່າ"}
              </Button>
            </div>
          </FormRoot>
        </QueryState>
        </div>
      </Main>
    </>
  );
}

export default PackageNotificationsSettingsPage;
